import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { buscarCpfSocioVendaIaCpf, useCpfSocioVendaIaCpf, usePreviaVendaIaCpf } from '@/hooks/useVendaIaCpf';
import { Bloco, Par, Pares, Tile, Tiles, moeda } from './lead-cpf-blocos';

/**
 * PRÉVIA DA VENDA IA CPF (Douglas 26/09/2026): o TOP do lead e o pacote que a
 * Conecta leva — "por apenas R$ X a mais você ganha tudo isso". Sai do mailing
 * (RENOV) ou do parque Vivo (PORT); na RENOV a proposta real só nasce quando a
 * conta chegar da gerente. Os números vêm de `venda_ia_cpf_previa`, os mesmos
 * que ordenam a fila.
 */
const PRACA: Record<string, string> = {
  MD1: 'Mercado em Desenvolvimento 1', MD2: 'Mercado em Desenvolvimento 2',
  ESPECIAL: 'Especial', ESPECIAL_PLUS: 'Especial+', GM: 'Grande Mercado (Padrão)',
};

export function LeadPreviaVendaIaCpf({ leadId }: { leadId: string }) {
  const { data: previa, isLoading, error } = usePreviaVendaIaCpf(leadId);
  if (isLoading) return null;
  if (error) {
    return <p role="alert" className="text-sm text-destructive">Não foi possível carregar a prévia da Venda IA CPF. Feche o lead e abra de novo.</p>;
  }
  if (!previa) return null;

  const renov = previa.estrategia === 'VENDA_IA_CPF_RENOVACAO';
  const diferenca = Number(previa.diferenca ?? 0);
  const frase = diferenca > 0
    ? `Por apenas ${moeda(diferenca)} a mais na conta, ele ganha tudo isso`
    : `Ele ganha tudo isso pagando ${moeda(Math.abs(diferenca))} a menos`;

  return (
    <Bloco
      titulo={`Venda IA CPF · ${previa.top ? `TOP ${previa.top}` : 'fora dos TOPs'}`}
      meta={`${PRACA[previa.grupo ?? ''] ?? (previa.praca === 'MD' ? 'Mercado em Desenvolvimento' : 'Grande Mercado')}${previa.campanha ? ` · ${previa.campanha}` : ''}`}
    >
      <Tiles>
        <Tile rotulo={renov ? 'paga hoje (mailing)' : 'paga hoje nas linhas Vivo'} valor={moeda(previa.valor_hoje)} />
        <Tile rotulo="pacote da Conecta" valor={moeda(previa.valor_pacote)} />
        <Tile rotulo={diferenca > 0 ? 'a mais por mês' : 'a menos por mês'} valor={moeda(Math.abs(diferenca))} />
      </Tiles>
      {previa.entra ? (
        <div className="border-l-[3px] border-l-primary pl-2.5 text-[14px] font-semibold">{frase}</div>
      ) : (
        <p className="text-[12.5px] text-muted-foreground">Fora da fila: {previa.motivo}.</p>
      )}
      <ul className="space-y-1">
        {previa.pacote.filter((p) => p.item !== 'Extras').map((p) => (
          <li key={p.item} className="grid grid-cols-[minmax(0,1fr)_88px] gap-2 text-[13px]">
            <span className="truncate" title={typeof p.detalhe === 'string' ? `${p.item} · ${p.detalhe}` : p.item}>
              {p.item}{typeof p.detalhe === 'string' ? <span className="text-muted-foreground"> · {p.detalhe}</span> : null}
            </span>
            <span className="text-right font-display tabular-nums">{moeda(p.valor)}</span>
          </li>
        ))}
      </ul>
      {(() => {
        const extras = previa.pacote.find((p) => p.item === 'Extras')?.detalhe;
        return Array.isArray(extras) && extras.length ? <p className="text-[12px] text-muted-foreground">{extras.join(' · ')}</p> : null;
      })()}
      <Pares>
        <Par rotulo="linhas" valor={previa.linhas ?? '—'} />
        {renov ? <Par rotulo="tipo no mailing" valor={previa.tipo_cliente ?? '—'} /> : <Par rotulo="fixa Vivo" valor={previa.fixa_vivo ? 'tem' : 'não consta'} />}
        {!renov && <Par rotulo="cobertura (CEP da empresa)" valor={previa.cobertura ? 'na lista Claro' : 'fora da lista'} />}
      </Pares>
      {!renov && <CpfDoSocio leadId={leadId} />}
      <p className="text-[11px] text-muted-foreground">
        {renov
          ? 'Prévia do mailing, só para escolher quem vai para a gerente. A proposta real sai da conta móvel e da fixa que ela devolver.'
          : 'Prévia do parque Vivo. A instalação pode ser em qualquer endereço: a Conecta confirma o CEP de instalação antes da proposta.'}
      </p>
    </Bloco>
  );
}

const STATUS_ASSERTIVA: Record<string, string> = {
  PENDENTE: 'na fila da Assertiva',
  PROCESSANDO: 'consultando na Assertiva',
  CONCLUIDA: 'a Assertiva respondeu, mas sem sócio com CPF válido',
  SEM_DADOS: 'a Assertiva não achou sócio para este CNPJ',
};

/** Empresa baixada ainda sem o CPF do sócio: a consulta à Assertiva é lead a lead (franquia limitada). */
function CpfDoSocio({ leadId }: { leadId: string }) {
  const qc = useQueryClient();
  const { data } = useCpfSocioVendaIaCpf(leadId);
  const [pedindo, setPedindo] = useState(false);
  if (!data || data.tem_cpf) return null;
  const emAndamento = data.status_assertiva === 'PENDENTE' || data.status_assertiva === 'PROCESSANDO';
  const pedir = async () => {
    setPedindo(true);
    try {
      await buscarCpfSocioVendaIaCpf(leadId);
      toast.success('Pedido à Assertiva. O CPF entra no lead assim que ela responder.');
      await qc.invalidateQueries({ queryKey: ['venda-ia-cpf', 'cpf-socio', leadId] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Não foi possível pedir o CPF à Assertiva.');
    } finally {
      setPedindo(false);
    }
  };
  return (
    <div className="flex items-center gap-3 border-l-[3px] border-l-primary pl-2.5">
      <p className="min-w-0 flex-1 text-[12.5px]">
        <span className="font-semibold">Sem o CPF do sócio.</span>{' '}
        <span className="text-muted-foreground">
          {data.status_assertiva ? STATUS_ASSERTIVA[data.status_assertiva] ?? data.status_assertiva : 'A proposta e o pedido precisam dele.'}
        </span>
      </p>
      {!emAndamento && (
        <button
          type="button"
          onClick={pedir}
          disabled={pedindo}
          className="shrink-0 rounded-lg border border-border px-2.5 py-1.5 text-[12.5px] font-semibold hover:border-primary hover:text-primary disabled:opacity-60"
        >
          {pedindo ? 'Pedindo…' : 'Buscar CPF na Assertiva'}
        </button>
      )}
    </div>
  );
}
